import { useMemo } from 'react';
import { type ExpressionValue } from '../../evaluate/ExpressionValue.ts';
import { getExpressionValue } from '../../evaluate/getExpressionValue.ts';
import { type IExpression } from '../../IExpression.ts';
import { useMaterializedExpressionReferences } from './useMaterializedExpressionReferences.tsx';
import { useWatchReferences } from './useWatchReferences.tsx';

export type IUseExpressionValue =
  // Calcolo del valore eseguito correttamente
  | { type: 'value'; result: ExpressionValue }
  // Espressione non definita
  | { type: 'null' };

export interface IUseExpressionValueOptions {
  skip?: boolean | ((variables: Record<string, any>) => boolean);
}

/**
 * Ottiene il risultato di una espressione. Per essere calcolato ha
 * bisogno di:
 * 1. Essere all'interno di un FormProvider
 * 2. Avere un nome (name) che sia un path valido per poter calcolare correttamente
 *    le dipendenze in fase di `useWatch`
 *
 * @param name Percorso da quale devono essere risolte le dipendenze
 * @param expression Espressione da valutare
 */
export function useExpressionValue(
  name: string,
  expression: IExpression | null | undefined,
  options?: IUseExpressionValueOptions,
): IUseExpressionValue {
  const { references } = useMaterializedExpressionReferences(name, expression);

  const variables = useWatchReferences(references);

  return useMemo(() => {
    if (
      options?.skip === true ||
      (typeof options?.skip === 'function' && options.skip(variables))
    ) {
      return { type: 'null' as const };
    }

    return getExpressionValue({ name, expression, references, variables });
  }, [name, expression, references, variables, options]);
}
